import ConnexionDetector from '@/components/ConnexionDetector/ConnexionDetector';
import AppErrorBoundary from '@/components/Error-boundry/error-boundary';
import Header from '@/components/Nav/Nav';
import PageLoader from '@/components/pageLoader/PageLoader';
import { BASE_URL } from '@/env';
import { PrivateRoute } from '@/navigation/RouteType';
import Page404 from '@/pages/404/404';
import Errorpage from '@/pages/ErrorPage/ErrorPage';
import FailureComponent from '@/pages/ErrorPage/FailureComponent';
import { RootState } from '@/store/store';
import { NavPaths } from '@/values/NavPaths';

import { Suspense, lazy, useEffect, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useSelector } from 'react-redux';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';

import * as Sentry from '@sentry/react';
import { Toast } from '@sobrus-com/sobrus-design-system-v2';
import { Review } from '@sobrus-com/sobrus-surveys-package';
import Aos from 'aos';
import 'aos/dist/aos.css';

// import ErrorPage from './ErrorPage';

const Signin = lazy(() => import('@/pages/Signin/Signin'));
const CountryVerification = lazy(() => import('@/navigation/CountryVerification'));
const DeniedAccess = lazy(() => import('@/pages/DeniedAccess/DeniedAccess'));
const PremiumFeats = lazy(() => import('@/pages/premium/PremiumFeats'));
const Offers = lazy(() => import('@/pages/achats/offers/Offers'));
const OrderAdd = lazy(() => import('@/pages/achats/offers/orderAdd/OrderAdd'));
const OrderAddStep1 = lazy(() => import('@/pages/achats/offers/orderAdd/OrderAddStep1'));
const OrderAddStep2 = lazy(() => import('@/pages/achats/offers/orderAdd/OrderAddStep2'));
const OrderAddStep3 = lazy(() => import('@/pages/achats/offers/orderAdd/OrderAddStep3'));
const VOrderAdd = lazy(() => import('@/pages/ventes/CommandesClients/marketplaceOrderAdd/VOrderAdd'));
const VOrderAddStep1 = lazy(() => import('@/pages/ventes/CommandesClients/marketplaceOrderAdd/VOrderAddStep1'));
const VOrderAddStep2 = lazy(() => import('@/pages/ventes/CommandesClients/marketplaceOrderAdd/VOrderAddStep2'));
const VOrderAddStep3 = lazy(() => import('@/pages/ventes/CommandesClients/marketplaceOrderAdd/VOrderAddStep3'));
const ImportClients = lazy(() => import('@/pages/clients/importClients/ImportClients'));
const ImportClientsStep1 = lazy(() => import('@/pages/clients/importClients/ImportClientsStep1'));
const ImportClientsStep2 = lazy(() => import('@/pages/clients/importClients/ImportClientsStep2'));
const ImportClientsStep3 = lazy(() => import('@/pages/clients/importClients/ImportClientsStep3'));
const ImportClientsResults = lazy(() => import('@/pages/clients/importClients/ImportClientsResults'));
const ImportsHistory = lazy(() => import('@/pages/clients/importClients/ImportsHistory'));
const ImportHistoryDetails = lazy(() => import('@/pages/clients/importClients/ImportHistoryDetails'));
const ImportProducts = lazy(() => import('@/pages/produits/importProducts/ImportProducts'));
const ImportProductsStep1 = lazy(() => import('@/pages/produits/importProducts/ImportProductsStep1'));
const ImportProductsStep2 = lazy(() => import('@/pages/produits/importProducts/ImportProductsStep2'));
const ImportProductsStep3 = lazy(() => import('@/pages/produits/importProducts/ImportProductsStep3'));
const ImportProductsResults = lazy(() => import('@/pages/produits/importProducts/ImportProductsResults'));
const ImportProductsHistory = lazy(() => import('@/pages/produits/importProducts/history/ImportProductsHistory'));
const ImportProductsDetails = lazy(() => import('@/pages/produits/importProducts/details/ImportProductDetails'));
const ClientDemandsListPage = lazy(() => import('@/clientDemands/pages/ClientDemandsListPage'));

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const HomeNavigation = () => {
	const isLogin = useSelector((state: RootState) => state.user.isLogin);
	const user = useSelector((state: RootState) => state.user.user);
	const location = useLocation();
	const navigate = useNavigate();

	const [showNav, setShowNav] = useState(false);

	useEffect(() => {
		setTimeout(() => {
			setShowNav(true);
		}, 100);

		Aos.init({
			anchorPlacement: 'bottom-center',
		});

		window.addEventListener('load', Aos.refresh);

		return () => {
			window.removeEventListener('load', Aos.refresh);
		};
	}, []);

	useHotkeys(
		'F3',
		() => {
			navigate('/ventes/commandes-clients/creation');
		},
		[],
		{ preventDefault: true, enableOnFormTags: true }
	);

	return (
		<div className="sns">
			{/* <ConnexionDetector /> */}
			<Toast />
			{isLogin && window.location.pathname !== '/404' && showNav && <Header user={user} />}
			{isLogin && window.location.pathname !== '/denied_access' && window.location.pathname !== '/404' && (
				<Review className="" baseUrl={BASE_URL} backgroundColor="#756DC5" />
			)}
			<AppErrorBoundary>
				<SentryRoutes>
					<Route path="/" element={<Navigate to="/home" replace />} />
					{NavPaths.map(
						({ path, Component, canDoes, premium }, index) =>
							!(user?.subscriptionType === 'free' && premium) && (
								<Route
									key={index + 'route1'}
									path={path}
									element={
										<Suspense fallback={<PageLoader />}>
											<PrivateRoute component={<Component />} canDoes={canDoes ?? []} />
										</Suspense>
									}
								/>
							)
					)}

					<Route path="/clients/import" element={<PrivateRoute component={<ImportClients />} canDoes={[]} />}>
						<Route index element={<ImportClientsStep1 />} />
						<Route path="/clients/import/step1" element={<ImportClientsStep1 />} />
						<Route path="/clients/import/step2" element={<ImportClientsStep2 />} />
						<Route path="/clients/import/step3/:id" element={<ImportClientsStep3 />} />
						<Route path="/clients/import/results/:id" element={<ImportClientsResults />} />
					</Route>
					<Route
						path="/clients/import/history"
						element={
							<PrivateRoute
								component={
									<Suspense fallback={<PageLoader />}>
										<ImportsHistory />
									</Suspense>
								}
								canDoes={[]}
							/>
						}
					/>
					<Route
						path="/clients/import/history/:id"
						element={
							<PrivateRoute
								component={
									<Suspense fallback={<PageLoader />}>
										<ImportHistoryDetails />
									</Suspense>
								}
								canDoes={[]}
							/>
						}
					/>

					{/* bulk import products */}
					<Route
						path="/produits/import"
						element={<PrivateRoute component={<ImportProducts />} canDoes={[]} />}
					>
						<Route index element={<ImportProductsStep1 />} />
						<Route path="step1" element={<ImportProductsStep1 />} />
						<Route path="step2" element={<ImportProductsStep2 />} />
						<Route path="step3/:id" element={<ImportProductsStep3 />} />
						<Route path="results/:id" element={<ImportProductsResults />} />
					</Route>
					<Route
						path="/produits/import/details/:id"
						element={
							<PrivateRoute
								component={
									<Suspense fallback={<PageLoader />}>
										<ImportProductsDetails />
									</Suspense>
								}
								canDoes={[]}
							/>
						}
					/>
					<Route
						path="/produits/import/history"
						element={
							<PrivateRoute
								component={
									<Suspense fallback={<PageLoader />}>
										<ImportProductsHistory />
									</Suspense>
								}
								canDoes={[]}
							/>
						}
					/>
					<Route
						path="/achats/offers"
						element={
							<Suspense fallback={<PageLoader />}>
								<PrivateRoute
									component={
										<Suspense fallback={<PageLoader />}>
											<Offers />
										</Suspense>
									}
									canDoes={['get_wholesaler_offers_list']}
								/>
							</Suspense>
						}
					/>
					{/* Achats Orders */}
					<Route
						path="/achats/offers/details/:id"
						element={
							<Suspense fallback={<PageLoader />}>
								<PrivateRoute component={<OrderAdd />} canDoes={['get_wholesaler_offer_by_id']} />
							</Suspense>
						}
					>
						<Route index element={<Navigate to="step1" replace />} />
						<Route
							path="step1"
							element={
								<Suspense fallback={<PageLoader />}>
									<OrderAddStep1 />
								</Suspense>
							}
						/>
						<Route
							path="step2"
							element={
								<Suspense fallback={<PageLoader />}>
									<OrderAddStep2 />
								</Suspense>
							}
						/>
						<Route
							path="step3"
							element={
								<Suspense fallback={<PageLoader />}>
									<OrderAddStep3 />
								</Suspense>
							}
						/>
					</Route>
					{/* Ventes Orders */}
					<Route
						path="/ventes/commandes-clients/marketplace"
						element={
							<Suspense fallback={<PageLoader />}>
								<VOrderAdd />
							</Suspense>
						}
					>
						<Route index element={<Navigate to="/ventes/commandes-clients/marketplace/step1" />} />
						<Route
							path="/ventes/commandes-clients/marketplace/step1"
							element={
								<Suspense fallback={<PageLoader />}>
									<VOrderAddStep1 />
								</Suspense>
							}
						/>
						<Route
							path="/ventes/commandes-clients/marketplace/step2"
							element={
								<Suspense fallback={<PageLoader />}>
									<VOrderAddStep2 />
								</Suspense>
							}
						/>
						<Route
							path="/ventes/commandes-clients/marketplace/step3"
							element={
								<Suspense fallback={<PageLoader />}>
									<VOrderAddStep3 />
								</Suspense>
							}
						/>
					</Route>
					<Route
						path="/premium"
						element={
							<PrivateRoute
								component={
									<Suspense fallback={<PageLoader />}>
										<PremiumFeats />
									</Suspense>
								}
								canDoes={[]}
							/>
						}
					/>
					{/* Voice of Users */}
					<Route
						path="/voice-of-users"
						element={
							<Suspense fallback={<PageLoader />}>
								<ClientDemandsListPage />
							</Suspense>
						}
					/>
					{/* SignIn */}
					<Route
						path="/signin"
						element={
							<Suspense fallback={<PageLoader />}>
								<Signin />
							</Suspense>
						}
					/>
					<Route
						path="/auth/country-verification"
						element={
							<Suspense fallback={<PageLoader />}>
								<CountryVerification />
							</Suspense>
						}
					/>
					<Route
						path="/denied_access"
						element={
							<Suspense fallback={<PageLoader />}>
								<DeniedAccess />
							</Suspense>
						}
					/>
					<Route
						path="/auth/failed"
						element={
							<Suspense fallback={<PageLoader />}>
								<FailureComponent />
							</Suspense>
						}
					/>
					<Route
						path="/error"
						element={
							<Suspense fallback={<PageLoader />}>
								<Errorpage />
							</Suspense>
						}
					/>
					<Route
						path="*"
						element={
							<Suspense fallback={<PageLoader />}>
								<Page404 />
							</Suspense>
						}
					/>
				</SentryRoutes>
			</AppErrorBoundary>
		</div>
	);
};

export default HomeNavigation;
